// ============================================================
// Lightweight markdown pipe for chat rendering.
// Handles: **bold**, _italic_, lists, tables, line breaks.
// Drops everything else. Good enough for the curated answers.
//
// Place at: app/components/skyplus/intelligence/markdown.pipe.ts
// ============================================================

import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({ name: 'markdown' })
export class MarkdownPipe implements PipeTransform {

  constructor(private sanitizer: DomSanitizer) {}

  transform(md: string): SafeHtml {
    if (!md) return '';

    let html = this.escapeHtml(md);

    // Tables (very simple: line by line)
    html = this.renderTables(html);

    // Bold and italic
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/_([^_\n]+?)_/g, '<em>$1</em>');

    // Unordered lists
    html = this.renderLists(html);

    // Paragraphs / line breaks
    html = html.split(/\n{2,}/).map(block => {
      if (block.trim().startsWith('<')) return block;
      return '<p>' + block.replace(/\n/g, '<br>') + '</p>';
    }).join('');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private renderLists(html: string): string {
    const lines = html.split('\n');
    const out: string[] = [];
    let inList = false;
    for (const line of lines) {
      if (/^[-*]\s+/.test(line)) {
        if (!inList) { out.push('<ul>'); inList = true; }
        out.push('<li>' + line.replace(/^[-*]\s+/, '') + '</li>');
      } else {
        if (inList) { out.push('</ul>'); inList = false; }
        out.push(line);
      }
    }
    if (inList) out.push('</ul>');
    return out.join('\n');
  }

  private renderTables(html: string): string {
    const lines = html.split('\n');
    const out: string[] = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (/^\|.+\|$/.test(line.trim()) && i + 1 < lines.length && /^\|\s*[-:]+/.test(lines[i + 1].trim())) {
        // Found table header
        const header = this.splitRow(line);
        i += 2; // skip separator row
        const rows: string[][] = [];
        while (i < lines.length && /^\|.+\|$/.test(lines[i].trim())) {
          rows.push(this.splitRow(lines[i]));
          i++;
        }
        out.push('<table>');
        out.push('<thead><tr>' + header.map(c => '<th>' + c + '</th>').join('') + '</tr></thead>');
        out.push('<tbody>');
        rows.forEach(r => {
          out.push('<tr>' + r.map(c => '<td>' + c + '</td>').join('') + '</tr>');
        });
        out.push('</tbody></table>');
        continue;
      }
      out.push(line);
      i++;
    }
    return out.join('\n');
  }

  private splitRow(line: string): string[] {
    return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(s => s.trim());
  }
}
